import React, { useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useModal } from 'hooks/useModal';
import { grey } from '@mui/material/colors';
import Tooltip from '@mui/material/Tooltip';
import { Launch } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { ModalsKeys, QueryListResponse } from 'global';
import { FormInput, FormCheckbox } from 'components/form';
import { ListEmpty } from 'components/shared/List/ListEmpty';
import { DialogHeader } from 'components/shared/Dialog/DialogHeader';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  Box,
  Stack,
  Dialog,
  Button,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';

import { internalInventoryApi } from '../../api';
import { formatCurrency } from '../../../../utils';
import { NO_DATA } from '../../../../constants/common';
import { capitalizeFirstLetter } from '../../../../formatters';

export const SearchInventoryDialog = () => {
  const {
    isOpen,
    handleClose,
    data: { onSubmit },
  } = useModal(ModalsKeys.SearchInventoryDialog);

  const history = useHistory();

  const [search, setSearch] = useState<string>('');
  const [selectedList, setSelectedList] = useState<string[]>([]);

  const [onSearch, { loading, data, previousData }] = useLazyQuery<
    QueryListResponse<{
      id: string;
      equipment: {
        id: string;
        price: { wholesaleCost: number | null; totalWholesaleCost: number | null };
        details: {
          make: string;
          vin: string;
          year: string;
          model: string;
          name: string;
          class: string;
          mileage: string;
          stockId: string;
          condition: string;
          description: string;
        };
      };
    }>
  >(internalInventoryApi.getFullList);

  const searchResult = data?.result?.list ?? previousData?.result?.list ?? [];

  const debouncedSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        await onSearch({ variables: { input: { search: value.trim().toLowerCase() } } });
      }, 400),
    [onSearch]
  );

  return (
    <Dialog open={isOpen} maxWidth="xl" fullWidth onClose={handleClose}>
      <DialogHeader label="Search Equipment" handleClose={handleClose} />

      <DialogContent dividers>
        <Stack gap="1rem" padding="0.5rem 0" sx={{ position: 'relative' }}>
          <FormInput
            value={search}
            variant="outlined"
            label="Search by Stock#, VIN, make, model, or name"
            onChange={async value => {
              setSearch(value);

              if (value) await debouncedSearch(value);
              else setSelectedList([]);
            }}
          />

          {loading && <LinearProgress sx={{ width: '100%', position: 'absolute', top: '45px' }} />}

          <Stack direction="column" gap="1rem" sx={{ height: '20rem', overflowY: 'auto' }}>
            <RenderWithCondition
              condition={!!search && !!searchResult.length}
              fallBack={
                <ListEmpty
                  label={
                    <Typography color="textSecondary" variant="h6" component="span">
                      Start search
                    </Typography>
                  }
                />
              }
            >
              {searchResult.map(({ id, equipment }) => (
                <Stack
                  key={id}
                  gap="0.5rem"
                  width="100%"
                  direction="row"
                  alignItems="center"
                  sx={{ borderBottom: `1px solid ${grey[300]}` }}
                >
                  <Box sx={{ flex: 1 }}>
                    <FormCheckbox
                      value={selectedList.includes(id)}
                      label={[
                        capitalizeFirstLetter(equipment.details.condition),
                        equipment.details.name,
                        equipment.details.vin,
                        `${equipment.details.mileage} miles`,
                      ]
                        .filter(Boolean)
                        .join(' - ')}
                      onChange={() => {
                        setSelectedList(list =>
                          list.includes(id) ? list.filter(listItem => listItem !== id) : [...list, id]
                        );
                      }}
                    />

                    <Typography sx={{ marginLeft: '1.5rem' }} variant="subtitle1" fontWeight="bold">
                      {formatCurrency(equipment.price.totalWholesaleCost)}
                    </Typography>
                  </Box>

                  <Typography variant="caption">Stock #: {equipment.details.stockId ?? NO_DATA}</Typography>

                  <Tooltip title="Visit details page">
                    <IconButton
                      size="small"
                      onClick={() => {
                        history.push(`/equipment/${equipment.id}`);
                      }}
                    >
                      <Launch color="primary" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </RenderWithCondition>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>

        <Button
          variant="contained"
          onClick={() => {
            onSubmit(selectedList).then(handleClose);
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
